import React, { useEffect, useMemo, useRef } from 'react';
import _ from 'lodash';
import ReactHtmlParser from 'react-html-parser';
import { PageCertificate, PlayerLanguages } from '@scormhero/shared';
import { format, isValid, parseISO } from 'date-fns';
import ptLocale from 'date-fns/locale/pt-BR';
import esLocale from 'date-fns/locale/es';
import itLocale from 'date-fns/locale/it';
import deLocale from 'date-fns/locale/de';
import frLocale from 'date-fns/locale/fr';
import ruLocale from 'date-fns/locale/ru';
import skLocale from 'date-fns/locale/sk';
import huLocale from 'date-fns/locale/hu';
import svLocale from 'date-fns/locale/sv';

import styles from './CertificateItself.module.scss';

type CertificateItselfProps = PageCertificate & {
  onMount: (arg: HTMLDivElement) => void;
  globalState: any;
};

export const formatCertificateDate = (
  dateAsISOString: string,
  language?: PlayerLanguages
): string => {
  let completedAt: Date = parseISO(new Date().toISOString());

  try {
    completedAt = parseISO(
      typeof dateAsISOString === 'string'
        ? dateAsISOString
        : new Date().toISOString()
    );

    // check invalid date - if so, get today's date
    if (!isValid(completedAt)) completedAt = parseISO(new Date().toISOString());
  } catch (err) {
    // do not change initial value
    // (it assumes today's date)
  }
  switch (language) {
    case 'pt-BR':
      return format(completedAt, 'dd MMMM yyyy', {
        locale: ptLocale,
      });
    case 'es-ES':
      return format(completedAt, 'dd MMMM yyyy', {
        locale: esLocale,
      });
    case 'it-IT':
      return format(completedAt, 'dd MMMM yyyy', {
        locale: itLocale,
      });
    case 'de-DE':
      return format(completedAt, 'dd MMMM yyyy', {
        locale: deLocale,
      });
    case 'fr-FR':
      return format(completedAt, 'dd MMMM yyyy', {
        locale: frLocale,
      });
    case 'ru-RU':
      return format(completedAt, 'dd MMMM yyyy', {
        locale: ruLocale,
      });
    case 'hu-HU':
      return format(completedAt, 'dd MMMM yyyy', {
        locale: huLocale,
      });
    case 'sk-SK':
      return format(completedAt, 'dd MMMM yyyy', {
        locale: skLocale,
      });
    case 'sv-SV':
      return format(completedAt, 'dd MMMM yyyy', {
        locale: svLocale,
      });
    default:
      return format(completedAt, 'MMM do yyyy');
  }
};

export const sanitizeStudentNameOnCertificate = (name: string) => {
  try {
    name = name.trim();
    if (name.substring(0, 1) === ',')
      name = name.substring(1, name.length).trim();
  } catch (err) {
    // do nothing
  }
  return name;
};

function CertificateItself(props: CertificateItselfProps) {
  const { getTracking, course = {}, scorm = {} } = props.globalState;
  const courseTraking = getTracking(course.courseId);
  const { studentName = 'Name' } = scorm.initialScormData;
  const certificateRef = useRef<HTMLDivElement>(null);
  const color = (props.certificate.color || '#999999').replace('#', '');

  useEffect(() => {
    certificateRef.current && props.onMount(certificateRef.current);
  }, []); // eslint-disable-line

  const message = useMemo(() => {
    let updatedMessage = props.certificate.message;
    if (course?.name)
      updatedMessage = _.replace(
        updatedMessage,
        '[[COURSE_NAME]]',
        `<strong>${course?.name}</strong>`
      );
    updatedMessage = _.replace(
      updatedMessage,
      '[[COMPLETION_DATE]]',
      `<strong>${formatCertificateDate(
        courseTraking?.completedAt,
        course?.language
      )}</strong>`
    );
    return ReactHtmlParser(updatedMessage);
  }, [courseTraking, course]); // eslint-disable-line

  return (
    <div
      className={styles.root}
      ref={certificateRef}
      style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 16 16' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h16v2h-6v6h6v8H8v-6H2v6H0V0zm4 4h2v2H4V4zm8 8h2v2h-2v-2zm-8 0h2v2H4v-2zm8-8h2v2h-2V4z' fill='%23${color}' fill-opacity='1' fill-rule='evenodd'/%3E%3C/svg%3E")`,
      }}
    >
      <div className={styles.inner}>
        {props.certificate.logo?.name ? (
          <div
            className={styles.logo}
            style={{
              backgroundImage: props.certificate.logo?.name
                ? `url('${course.baseURL}/${props.certificate.logo?.name}')`
                : undefined,
            }}
            data-testid="logo"
          />
        ) : null}
        <h2>{props.title}</h2>
        <div className={styles.main}>
          <h1>{sanitizeStudentNameOnCertificate(studentName)}</h1>
          <p className={styles.description}>{message}</p>
        </div>
        <div className={styles.bottom}>
          {!_.isEmpty(props.certificate.instructorSignature) ? (
            <p className={styles.signature}>
              {props.certificate.instructorSignature}
            </p>
          ) : null}
          <p className={styles.instructor}>
            {!_.isEmpty(props.certificate.instructorName) ? (
              <>
                <span className={styles.instructorName}>
                  {props.certificate.instructorName}
                </span>{' '}
                <br />
              </>
            ) : null}
            <span>{props.certificate.instructorJob}</span>
          </p>
        </div>
      </div>
    </div>
  );
}

export default CertificateItself;
